/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Container from "@/src/components/Container/Container";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import PartnerLogoSection from "@/src/components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import LandingPageTeaser from "@/src/components/Teaser/LandingPageTeaser";
import AttorneySection from "@/src/components/Sections/Generic/AttorneySection/AttorneySection";
import { defaultProvenExpertTeaserCard } from "@/src/services/landingPageService";
import LinkedCardBox from "@/src/components/LinkedCardBox/LinkedCardBox";
import iconAttorneys from "@/src/assets/icons/generic/IconLawyers";
import iconDocument from "@/src/assets/icons/generic/IconDocument";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import HowItWorksSectionConsulting from "@/src/components/Sections/FamilyLaw/Consulting/HowItWorksSection/HowItWorksSectionConsulting";
import LpCivilServiceLawMeta from "./LpCivilServiceLawMeta";
import PartnerCheckin from "@/src/components/Checkin/PartnerCheckin/PartnerCheckin";
import Grid from "@mui/material/Grid";
import applicationFormStyle from "@/src/components/Form/applicationFormStyle";
import { isPhoneNumberDefault, minStringLength, requiredFieldDefault, requiredValidator } from "@/src/services/validationRules";
import { translate } from "@/src/services/translations/translations";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import MenuItem from "@mui/material/MenuItem";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";

type LpCivilServiceLawPartnerProps = {
  partner: NonNullable<AbstractCase["acquisitionPartner"]>;
  preCheckinSubmit?: (values: Record<string, any>) => Promise<boolean>;
  externalError?: string;
};

const LpCivilServiceLawPartner = ({ partner, preCheckinSubmit, externalError }: LpCivilServiceLawPartnerProps) => {
  const { isLoggedIn } = useCustomer();

  const additionalFieldsConfig = [
    {
      key: "customer.person.gender",
      initialValue: "",
      fieldToRender: (
        <Grid
          sx={applicationFormStyle.gridSpaceMd}
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorSelectField
            displayEmpty
            label={translate("customer.person.gender.label", {})}
            name={`customer.person.gender`}
          >
            <MenuItem value={""}>Keine Anrede</MenuItem>
            <MenuItem value={"female"}>Frau</MenuItem>
            <MenuItem value={"male"}>Herr</MenuItem>
          </ValidatorSelectField>
        </Grid>
      ),
    },
    {
      key: "customer.person.givenName",
      initialValue: "",
      fieldToRender: (
        <Grid
          sx={applicationFormStyle.gridSpaceMd}
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorTextField
            name="customer.person.givenName"
            label={translate("customer.person.givenName.label", {})}
            validators={[
              requiredValidator("Bitte geben Sie den Vornamen an"),
              { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Eingabe" },
            ]}
          />
        </Grid>
      ),
    },
    {
      key: "customer.person.familyName",
      initialValue: "",
      fieldToRender: (
        <Grid
          sx={applicationFormStyle.gridSpaceMd}
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ValidatorTextField
            name="customer.person.familyName"
            label={translate("customer.person.familyName.label", {})}
            validators={[
              requiredValidator("Bitte geben Sie den Nachnamen an"),
              { validator: minStringLength, params: [2], message: "Bitte kontrollieren Sie Ihre Eingabe" },
            ]}
          />
        </Grid>
      ),
    },
    {
      key: "customer.telephone",
      initialValue: "",
      fieldToRender: (
        <Grid
          sx={applicationFormStyle.gridSpaceMd}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ValidatorTextField
            name="customer.telephone"
            label={translate("customer.telephone.label", {})}
            autoComplete="tel"
            validators={[requiredValidator("Bitte geben Sie Ihre Telefonnummer an"), ...isPhoneNumberDefault]}
          />
        </Grid>
      ),
    },
    {
      key: "caseInsurance.insurancePolicyNumber",
      initialValue: "",
      fieldToRender: (
        <Grid
          sx={applicationFormStyle.gridSpaceMd}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ValidatorTextField
            name="caseInsurance.insurancePolicyNumber"
            label={translate("caseInsurance.insurancePolicyNumber.label", {})}
            validators={[requiredFieldDefault]}
          />
        </Grid>
      ),
    },
  ];

  return (
    <LayoutPublicRoutes minimal={!isLoggedIn} clickableLogo={isLoggedIn}>
      <LpCivilServiceLawMeta />
      <LandingPageTeaser
        teaserButtonText={"Jetzt starten"}
        headline={"Professionelle Hilfe im Beamtenrecht"}
        subheadline={"Wir sorgen dafür, dass Sie zu Ihrem Recht gelangen!"}
        teaserButtonLink={isLoggedIn ? "/servicewelt" : "#top-checkin"}
      >
        <LinkedCardBox
          linkedCards={[
            defaultProvenExpertTeaserCard,
            {
              icon: iconAttorneys,
              heading: "Professionelle Beratung",
            },
            {
              icon: iconDocument,
              heading: "Unkomplizierte Abwicklung",
            },
          ]}
        />
      </LandingPageTeaser>
      <PartnerLogoSection partnerIri={partner} containerStyle={"settlementContainer"} />
      {!isLoggedIn && (
        <Container>
          <PartnerCheckin
            idPrefix={"top"}
            partner={partner}
            checkinHeading={"Jetzt professionelle Unterstützung erhalten"}
            additionalFormFields={additionalFieldsConfig}
            preSubmitCallback={preCheckinSubmit}
            externalError={externalError}
            productName={"civilServiceLaw"}
          />
        </Container>
      )}
      <HowItWorksSectionConsulting />
      <AttorneySection
        subheading={"Sie möchten sich professionell und schnell unterstützen lassen? Wir helfen Ihnen - jederzeit und ortsunabhängig!"}
        heading={"Deutschlandweit tätige Partneranwälte"}
      />
      {!isLoggedIn && (
        <Container>
          <PartnerCheckin
            idPrefix={"top"}
            partner={partner}
            checkinHeading={"Jetzt professionelle Unterstützung erhalten"}
            additionalFormFields={additionalFieldsConfig}
            preSubmitCallback={preCheckinSubmit}
            externalError={externalError}
            productName={"civilServiceLaw"}
          />
        </Container>
      )}
    </LayoutPublicRoutes>
  );
};

export default LpCivilServiceLawPartner;
